'use client';

import './select.scss';

import { SelectHTMLAttributes } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { Either } from '@sola/schema/dotcom/helpers.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { cn } from '../../utils/helpers';
import {
  Field,
  FieldErrorIcon,
  FieldErrorMessage,
  FieldProps,
  FieldWrapper,
  getErrorElId,
} from '../field/field';

export interface SelectItemProps {
  disabled?: boolean;
  text?: string;
  value: string;
}

const SelectItems = ({ items }: { items: SelectItemProps[] }) =>
  items.map(({ value, disabled, text }) => (
    <option key={value} value={value} disabled={disabled}>
      {text || value}
    </option>
  ));

export interface SelectGroupProps {
  name: string;
  items: SelectItemProps[];
}

export type SelectProps = Partial<
  Omit<FieldProps, 'children' | 'min' | 'max' | 'valueAsNumber' | 'valueAsDate'>
> &
  // TODO: TS helper text is unclear here.
  Either<{ items: SelectItemProps[] }, { groups: SelectGroupProps[] }> & {
    'aria-labelledby'?: React.ComponentPropsWithoutRef<'select'>['aria-labelledby'];
    'aria-label'?: string;
    label?: string;
    name?: string;
    placeholder?: string;
    autoComplete?: SelectHTMLAttributes<HTMLSelectElement>['autoComplete'];
    selectRef?: React.MutableRefObject<HTMLSelectElement>;
    defaultValue?: string;
    selectClasses?: string;
  };
export const Select: React.FC<SelectProps> = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  groups,
  items,
  label,
  placeholder,
  autoComplete,
  selectRef,
  onChange,
  defaultValue,
  selectClasses,
  ...fieldProps
}) => {
  if (!items && !groups) {
    console.warn('Select component missing items');
    return null;
  }

  return (
    <Field {...(fieldProps as FieldProps)} controlRef={selectRef}>
      {({ controlProps, error, formMethods }) => {
        const errorElId = getErrorElId(controlProps.id);
        const handleValueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          formMethods?.setValue(controlProps.name as string, e.target.value, {
            shouldValidate: true,
          });
          if (!onChange) {
            return;
          }

          onChange(e);
        };

        return (
          <>
            {label && (
              <label className="form-label label" htmlFor={controlProps.id}>
                {label}
              </label>
            )}

            <FieldWrapper className="Select">
              <FieldErrorIcon error={error} />
              <div className="Select__caret" aria-hidden>
                <ArkIcon iconName="chevron-down" />
              </div>
              <select
                disabled={fieldProps.disabled}
                className={cn('Select__trigger form-control', selectClasses)}
                ref={controlProps.ref || selectRef}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-errormessage={errorElId}
                autoComplete={autoComplete}
                onClick={() => {
                  trackClick(
                    label || 'Unlabeled Select Input',
                    undefined,
                    'Select__trigger form-control',
                  );
                }}
                {...controlProps}
                onChange={handleValueChange}
                defaultValue={defaultValue}
              >
                {placeholder && <option value="">{placeholder}</option>}

                {items && <SelectItems items={items} />}

                {groups &&
                  groups.map((group) => (
                    <optgroup key={group.name} label={group.name}>
                      <SelectItems items={group.items} />
                    </optgroup>
                  ))}
              </select>
            </FieldWrapper>

            <FieldErrorMessage error={error} id={errorElId} />
          </>
        );
      }}
    </Field>
  );
};
